import { Button, Form, FormProps, Input, InputNumber, message, Select, DatePicker, Upload } from "antd";
import React, { useState } from "react";
import { MovieType } from "../pages/movies";
import { create, storage, update } from "../firebase";
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { chfsToStripeAmount, stripeAmountToChfs } from "../utils";

const { Option } = Select;
const { TextArea } = Input;

const MovieForm: React.FC<{ movie: MovieType, close: () => void }> = ({ movie, close }) => {
  const [loading, setLoading] = useState(false);

  const uploadImage = (file: File) => {
    if (!file || !file.name || !movie.id) {
      message.error('Fichier/film invalide ou manquant');
      return;
    }

    setLoading(true);
    const storageRef = ref(storage, `images/${movie.id}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log(`Upload is ${progress}% done`);
      },
      (error) => {
        setLoading(false);
        message.error('Téléchargement échoué');
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        await update("movies", movie.id, { image: url });
        setLoading(false);
        message.success('Téléchargement terminé');
        close();
      }
    );
  };

  const deleteImage = async () => {
    if (!movie.id || !movie.image) return;
    setLoading(true);
    await update("movies", movie.id, { image: null });
    await deleteObject(ref(storage, `images/${movie.id}`));
    setLoading(false);
    message.success("Image supprimée");
    close();
  };

  const onFinish: FormProps<MovieType>["onFinish"] = async (values) => {
    const formattedValues = {
      ...values,
      date: values.date?.format("YYYY-MM-DD").toString() ?? null,
      amount: chfsToStripeAmount(values.amount) || 0,
      goal: chfsToStripeAmount(values.goal),
    };
    if (movie.id) {
      const updatedMovie = { ...movie, ...formattedValues };
      await update("movies", movie.id, JSON.parse(JSON.stringify(updatedMovie)));
      message.success("Film mis à jour: " + updatedMovie.name);
    } else {
      await create("movies", Object.fromEntries(
        Object.entries(formattedValues).filter(([_, v]) => !!v)
      ));
      message.success("Film créé: " + formattedValues.name);
    }
    close();
  };

  return (
    <>
      <Form
        key={movie.id}
        name={movie.id}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        style={{ maxWidth: 600 }}
        initialValues={{
          name: movie.name,
          details: movie.details,
          amount: stripeAmountToChfs(movie.amount) || 0,
          goal: stripeAmountToChfs(movie.goal),
          status: movie.status,
          date: movie.date,
          description : movie.description,
          pseudo: movie.pseudo,
          image: movie.image,
          detailsProj: movie.detailsProj,
        }}
        onFinish={onFinish}
        autoComplete="off"
      >
        <Form.Item
          label="Nom du film"
          name="name"
          rules={[{ required: true, message: "Champ requis" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Montant actuel"
          name="amount"
          rules={[{ required: true, message: "Champ requis" }]}
        >
          <InputNumber />
        </Form.Item>
        <Form.Item label="Montant cible" name="goal">
          <InputNumber />
        </Form.Item>
        <Form.Item name="status" label="Statut" rules={[{ required: true, message: "Champ requis" }]}>
          <Select placeholder="Statut">
            <Option value="requested">Proposé</Option>
            <Option value="validated">Validé</Option>
            <Option value="canceled">Annulé</Option>
            <Option value="archived">Archivé</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Pseudo"
          name="pseudo"
          rules={[{ required: true, message: "Champ requis" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <TextArea
            autoSize={{ minRows: 3 }}
          />
        </Form.Item>

        <Form.Item
          label="Détails film"
          name="details"
        >
          <Input />
        </Form.Item>

        <Form.Item label="Date de projection" name="date">
          <DatePicker format="DD/MM/YYYY" placeholder="" />
        </Form.Item>

        <Form.Item
          label="Détails projection"
          name="detailsProj"
        >
          <Input />
        </Form.Item>

        {movie.id && <Form.Item label="Image" name="image">
          {movie.image ?
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={loading}
              disabled={loading}
              onClick={() => deleteImage()}
            >
                Supprimer l'image
            </Button> :
              <Upload
                customRequest={({ file, onSuccess }) => {
                  const uploadFile = file as File;
                  uploadImage(uploadFile);
                  onSuccess?.("ok")
                }}
                showUploadList={false}
                accept=".jpg,.jpeg,.png"
                maxCount={1}
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={loading}
                  disabled={loading}
                >
                  Charger une image
                </Button>
              </Upload>
          }
        </Form.Item>}

        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
          <Button type="primary" htmlType="submit">
            Sauvegarder
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

export default MovieForm;
